import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarPlot, ChartsAxisHighlight, ChartsLegend, ChartsXAxis, ChartsYAxis } from '@mui/x-charts';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { orderBy } from 'lodash';
import { BoxWithHeader } from '../common';
import { DealerStatistics } from '../types/statistics';

interface DealerAccidentsOverviewProps {
  graphData: DealerStatistics;
  date: string;
}

export const AllDealersRentalsCountOverview = ({ graphData, date }: DealerAccidentsOverviewProps) => {
  if (!graphData) return null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sortedArray = orderBy(
    graphData.totals.countRentalsByAllDealers.values,
    [(item) => Number(item?.types?.rent ?? -1), (item) => Number(item?.types?.waiting ?? -1)],
    ['desc', 'desc']
  );

  const waitingCounts = sortedArray.map((item) => item.types?.waiting ?? 0);
  const rentCounts = sortedArray.map((item) => item.types?.rent ?? 0);

  // --- zkrácení jména na mobilu
  const formatDealerName = (name?: string) => {
    if (!name) return '';
    if (!isMobile) return name;

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];

    const firstName = parts[0];
    const last = parts[parts.length - 1];
    const initial = last[0]?.toUpperCase() ?? '';

    return `${firstName} ${initial}.`;
  };

  const dealerLabels = sortedArray.map((item) => formatDealerName(item.name));

  // --- layout podle zařízení
  const layout = isMobile ? 'horizontal' : 'vertical';

  const xAxis = isMobile
    ? [
        {
          id: 'x-axis-id',
          scaleType: 'linear'
        } as const
      ]
    : [
        {
          id: 'x-axis-id',
          data: dealerLabels,
          scaleType: 'band'
        } as const
      ];

  const yAxis = isMobile
    ? [
        {
          id: 'y-axis-id',
          data: dealerLabels,
          scaleType: 'band'
        } as const
      ]
    : [
        {
          id: 'y-axis-id',
          scaleType: 'linear'
        } as const
      ];

  const margin = isMobile
    ? { left: 45, right: 10, top: 20, bottom: 90 }
    : { left: 40, right: 20, top: 20, bottom: 150 };

  return (
    <BoxWithHeader
      title={
        <>
          <Typography variant="h6" fontWeight="bold">
            Přehled zápůjček u obchodníků
          </Typography>
          <Typography variant="body2" color="gray">
            {`Celkový počet zápůjček v čase (${date})`}
          </Typography>
        </>
      }
    >
      <Box sx={{ width: '100%', height: 600 }}>
        <ResponsiveChartContainer
          skipAnimation
          margin={margin}
          series={[
            {
              type: 'bar',
              data: rentCounts,
              color: '#ffef59',
              label: 'Půjčené',
              layout
            },
            {
              type: 'bar',
              data: waitingCounts,
              color: '#81c784',
              label: 'Čekající',
              layout
            }
          ]}
          xAxis={xAxis}
          yAxis={yAxis}
        >
          <BarPlot borderRadius={2} barLabel="value" />

          {/* X osa – na desktopu jména, na mobilu hodnoty */}
          <ChartsXAxis
            position="bottom"
            axisId="x-axis-id"
            label={isMobile ? 'Počet zápůjček' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 10
                  }
                : {
                    angle: -70,
                    textAnchor: 'end',
                    fontSize: 9
                  }
            }
          />

          {/* Y osa – na mobilu jména, na desktopu číselná osa */}
          <ChartsYAxis
            position="left"
            axisId="y-axis-id"
            label={!isMobile ? 'Počet zápůjček' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 9,
                    angle: -40
                  }
                : {
                    textAnchor: 'end',
                    fontSize: 9
                  }
            }
          />

          <ChartsAxisHighlight y="band" />
          <ChartsLegend position={{ vertical: 'bottom', horizontal: 'middle' }} />
        </ResponsiveChartContainer>
      </Box>
    </BoxWithHeader>
  );
};
